import React, { FC } from "react";
import classNames from "classnames";
import { scoreCls } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { ConsumerVoiceAudit, CvReportMeta } from "../../../../../types/consumerVoice";
import { Micro } from "../components/ui";
import { cvObjectivePlaceholderCards, cvObjectivesRow, scoreCardsFor } from "./cvModel";
import type { CvCard } from "./cvModel";

/**
 * The six Consumer Voice score cards — a product-local copy of the top of the
 * report, src/components/ConsumerVoice/ConsumerVoiceReportView.tsx:
 *
 *   - IndexCard (:70-95): label, number out of 100, and under it the caption,
 *     or the placeholder when there is no number (:92);
 *   - the three top IndexCards (:715-742): Consumer Voice (overall_score, the
 *     subscores caption), Consumer Demand (demand_index), Content Opportunity
 *     (opportunity_index), their captions from `componentsLine` (:61-68) over
 *     the report meta's demand / opportunity blocks (:684-686);
 *   - ObjectiveScoreRow (:137-161): "Objective alignment", then Trust,
 *     Education and Hype, each "Label · Objective", captions from
 *     meta.objective_scores, hidden when the run has no objective score and
 *     the meta no blocks (:139-141). A run with an overall score and no
 *     objective score was scored before v2, so its report can't have the
 *     blocks: hidden at once, no skeleton (`cvScoredBeforeObjectives`). With
 *     no score of any kind that rule waits on the report, so while it loads
 *     the row is a skeleton of its own size (label and three small cards),
 *     and a failed report hides it (`cvObjectivesRow`).
 *
 * The captions that come from the report hold their full height from the
 * first paint (`Caption`, cvModel `ghosts`): the report landing never moves
 * what is below the section.
 *
 * Labels, numbers, captions and placeholders are cvModel `scoreCardsFor`,
 * which copies those lines with their line numbers. The report view is not
 * imported (IndexCard is private) and not edited. Like IndexCard, a card
 * carries no words of its own: the number (or "—") always followed by
 * "/ 100", then the caption or placeholder — no band word or chip.
 *
 * Styled in the page's register, not the report's: tokens only (dark is the
 * token swap), the number's colour from `scoreCls` (40 / 70, the page's one
 * banding — not the report's 45 / 70 `scoreTone` pill, :23-28, so a 40-44
 * reads amber here and red there), the page's Micro
 * label, `font-CircularStdBlack` numbers (renders Book, D2). The three large
 * cards and the three small ones each sit in an auto-fit grid (200px / 180px
 * minimum columns): three across when they fit, else two, else one. Unlike
 * the report's `flex-wrap` rows, a card that wraps keeps its column's width
 * instead of stretching across the row. On a phone every card takes the full
 * width, and nothing scrolls sideways (min(100%, …) columns, min-w-0 cards,
 * labels and captions wrap).
 *
 * Usage:
 *   <CvScoreCards run={cv.ready} meta={cvReportMeta(item.audit)} metaLoading={item.loading}
 *     metaFailed={item.error} />
 */

const OBJECTIVES_LABEL_ID = "product-cv-objectives";
const LIST = "m-0 p-0 list-none grid gap-3 min-w-0";
const gridOf = (min: number) => ({ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))` });

/** Every layer of a caption sits in the same grid cell: the cell is as tall as the tallest. */
const CELL = { gridArea: "1 / 1" } as const;
/**
 * Pulse bars in the shape of a text's lines: the text, transparent, with a
 * bar behind each line fragment (box-decoration-break clones the background
 * per line), 0.66em tall — 8px on a 12px caption.
 */
const GHOST_BARS: React.CSSProperties = {
  color: "transparent",
  backgroundImage: "linear-gradient(var(--track), var(--track))",
  backgroundSize: "100% 0.66em",
  backgroundPosition: "0 50%",
  backgroundRepeat: "no-repeat",
  borderRadius: 999,
  boxDecorationBreak: "clone",
  WebkitBoxDecorationBreak: "clone",
};
// select-none: the transparent words aren't there to be selected or copied.
const Bars: FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="animate-pulse motion-reduce:animate-none select-none" style={GHOST_BARS}>
    {children}
  </span>
);

/**
 * The line under the number (12px at 1.45, 8px above). A line the report
 * decides (`fromMeta`) holds its `ghosts` — every text it can land as —
 * invisibly in the same cell, so the box is as tall as the tallest from the
 * first paint, whatever the width: the report landing changes the words,
 * never the height, and nothing below moves. While the report loads, the
 * longest ghost shows as pulse bars. Only a `fromMeta` line is ever pending,
 * and every one carries ghosts (cvModel.test.ts locks that in).
 */
const Caption: FC<{ card: CvCard; pending: boolean }> = ({ card, pending }) => {
  const ghosts = card.ghosts || [];
  return (
    <p className="m-0 grid text-text-2" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 8 }}>
      {ghosts.map((ghost) => (
        <span key={ghost} className="invisible" style={CELL} aria-hidden="true">
          {ghost}
        </span>
      ))}
      {pending ? (
        <span style={CELL} aria-hidden="true">
          <Bars>{ghosts[0]}</Bars>
        </span>
      ) : (
        <span style={CELL}>{card.text}</span>
      )}
    </p>
  );
};

interface CardProps {
  card: CvCard;
  small?: boolean;
  /** The report is still loading and this card's line comes from it (`Caption`). */
  pending: boolean;
  /** The whole card as pulse bars in its own shape (the reserved Objective row). */
  skeleton?: boolean;
}

const ScoreCard: FC<CardProps> = ({ card, small = false, pending, skeleton = false }) => {
  const { value } = card;
  const bars = (text: React.ReactNode) => (skeleton ? <Bars>{text}</Bars> : text);
  return (
    <li
      className={classNames(
        "flex flex-col min-w-0 rounded-lg border border-border-token",
        small ? "p-3.5" : "p-4",
      )}
    >
      <Micro>{bars(card.label)}</Micro>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5" style={{ marginTop: small ? 8 : 10 }}>
        <span
          className={classNames(
            "font-CircularStdBlack tabular-nums leading-none",
            small ? "text-[22px]" : "text-[30px] sm:text-[34px]",
            scoreCls(value),
          )}
          style={{ letterSpacing: "-.02em" }}
        >
          {bars(value == null ? "—" : value)}
        </span>
        <span className="text-text-2" style={{ fontSize: 12 }}>
          {bars("/ 100")}
        </span>
      </div>
      {skeleton ? (
        <p className="m-0" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 8 }}>
          <Bars>{card.text}</Bars>
        </p>
      ) : (
        <Caption card={card} pending={pending} />
      )}
    </li>
  );
};

/**
 * The Objective alignment row while the report decides whether it shows. It
 * can only land as three placeholders (the run has no objective score:
 * `cvObjectivePlaceholderCards`), so this is that row, label and cards, as
 * pulse bars in their exact shape: at any width a row that lands keeps the
 * height it held. A row that doesn't land (no blocks) collapses.
 */
const ObjectivesSkeleton: FC = () => (
  <div className="flex flex-col gap-2 min-w-0" aria-hidden="true">
    <Micro>
      <Bars>Objective alignment</Bars>
    </Micro>
    <ul className={LIST} style={gridOf(180)}>
      {cvObjectivePlaceholderCards().map((card) => (
        <ScoreCard key={card.key} card={card} small pending={false} skeleton />
      ))}
    </ul>
  </div>
);

interface Props {
  /** The newest ready run (`latestReady`): the numbers and the subscores caption. */
  run: ConsumerVoiceAudit;
  /** Its report's meta (`cvReportMeta`); null while the report loads or when its call failed. */
  meta: CvReportMeta | null;
  /** The report is still loading: the captions that read the meta pulse until it lands. */
  metaLoading: boolean;
  /** The report call failed: Demand's and Opportunity's placeholders say "Details unavailable." */
  metaFailed?: boolean;
}

export const CvScoreCards: FC<Props> = ({ run, meta, metaLoading, metaFailed = false }) => {
  const { indices, objectives } = scoreCardsFor(run, meta, metaFailed);
  const objectivesRow = cvObjectivesRow(run, meta, metaLoading, metaFailed);
  const pending = (card: CvCard) => metaLoading && card.fromMeta;

  return (
    <div className="flex flex-col gap-[18px] min-w-0" aria-busy={metaLoading || undefined}>
      <ul className={LIST} style={gridOf(200)} aria-label="Consumer Voice scores">
        {indices.map((card) => (
          <ScoreCard key={card.key} card={card} pending={pending(card)} />
        ))}
      </ul>

      {objectivesRow === "reserve" && <ObjectivesSkeleton />}
      {objectivesRow === "show" && objectives && (
        <div className="flex flex-col gap-2 min-w-0">
          <Micro id={OBJECTIVES_LABEL_ID}>Objective alignment</Micro>
          <ul className={LIST} style={gridOf(180)} aria-labelledby={OBJECTIVES_LABEL_ID}>
            {objectives.map((card) => (
              <ScoreCard key={card.key} card={card} small pending={pending(card)} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
